import { parseDocument } from "yaml";
import type { JsonObject } from "@/api";
import { getAtPath } from "./recipeCoerce";
import { parseOverrideKey } from "./recipeOverrides";

// Random background 제거. dataset.image_transforms 목록의 RandomBackground 항목을 Hydra delete
// override(~dataset.image_transforms.N)로 표현한다. 제출은 항상 repo_config + overrides 이므로
// YAML 본문을 직접 지우지 않는다.

const IMAGE_TRANSFORMS_PATH = "dataset.image_transforms";

// 항목 판정. 문자열 항목 자체이거나, type/_target_/name/transform 필드 값이
// "RandomBackground" 또는 "*.RandomBackground" 인 경우다.
const MARKER_FIELDS = ["type", "_target_", "name", "transform"] as const;

function matchesMarker(value: string): boolean {
  return value === "RandomBackground" || value.endsWith(".RandomBackground");
}

function isRandomBackgroundItem(item: unknown): boolean {
  if (typeof item === "string") {
    return matchesMarker(item);
  }
  if (item === null || typeof item !== "object" || Array.isArray(item)) {
    return false;
  }
  return MARKER_FIELDS.some((field) => {
    const value = (item as Record<string, unknown>)[field];
    return typeof value === "string" && matchesMarker(value);
  });
}

// 파싱된 config 에 RandomBackground transform 이 있는지 판정한다. 체크박스 노출과 문구의 게이트다.
export function hasRandomBackgroundTransform(data: JsonObject): boolean {
  const transforms = getAtPath(data, IMAGE_TRANSFORMS_PATH);
  return Array.isArray(transforms) && transforms.some(isRandomBackgroundItem);
}

// 파싱된 config 에서 delete override 키를 만든다. 여러 개면 뒤 인덱스부터 지워야 앞의 삭제가 뒤
// 인덱스를 옮기지 않는다(Hydra override 적용 순서 안정화).
export function randomBackgroundDeleteOverrideKeys(data: JsonObject): string[] {
  const transforms = getAtPath(data, IMAGE_TRANSFORMS_PATH);
  if (!Array.isArray(transforms)) {
    return [];
  }
  return transforms
    .map((item, index) => (isRandomBackgroundItem(item) ? index : -1))
    .filter((index) => index >= 0)
    .sort((left, right) => right - left)
    .map((index) => `~${IMAGE_TRANSFORMS_PATH}.${index}`);
}

// 저장된 override 행이 transform 목록(또는 그 항목)을 바꾸는지 판정한다. BE 는 폼의 해제 행을 목록 교체
// 행으로 바꿔 저장한다. 그래서 저장본을 읽는 화면이 RandomBackground 해제 흔적일 수 있다고 알릴 때 쓴다.
export function isRandomBackgroundOverrideKey(key: string): boolean {
  const { path } = parseOverrideKey(key);
  return path === IMAGE_TRANSFORMS_PATH || path.startsWith(`${IMAGE_TRANSFORMS_PATH}.`);
}

// config 본문에서 delete override 키를 만든다. 파싱 불가·목록 없음·항목 없음이면 빈 배열이다.
export function randomBackgroundDeleteOverrideKeysFromYaml(body: string): string[] {
  const doc = parseDocument(body);
  if (doc.errors.length > 0) {
    return [];
  }
  const value = doc.toJS();
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }
  return randomBackgroundDeleteOverrideKeys(value as JsonObject);
}
